import { 
  collection, 
  doc, 
  query, 
  getDocs, 
  orderBy, 
  runTransaction,
  serverTimestamp,
  getDoc
} from "firebase/firestore";
import { firestore, auth } from "../backend/firebaseConfig";

const paymentsCollection = collection(firestore, "Payments");

// Add a credit or debit transaction for the current user
export const addUserTransaction = async ({ amount, type, description, paymentdata }) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'User not authenticated' };

    const userId = currentUser.uid;
    const userEmail = currentUser.email;
    const userDocRef = doc(paymentsCollection, userId);
    const transactionsRef = collection(userDocRef, 'transactions');

    await runTransaction(firestore, async (transaction) => {
      const userDoc = await transaction.get(userDocRef);
      let currentBalance = 0;

      if (userDoc.exists() && userDoc.data().currentBalance != null) {
        currentBalance = userDoc.data().currentBalance;
      }

      const isCredit = type === 'credit';
      const newBalance = isCredit ? currentBalance + amount : currentBalance - amount;

      if (!isCredit && newBalance < 0) {
        throw new Error('Insufficient balance');
      }

      const transactionData = {
        amount,
        type, // 'credit' or 'debit'
        balanceBefore: currentBalance,
        balanceAfter: newBalance,
        userId,
        userEmail,
        description: description || '',
        paymentdata: paymentdata || null,
        createdAt: serverTimestamp(),
      };

      const newTransactionRef = doc(transactionsRef);
      transaction.set(newTransactionRef, transactionData);
      transaction.set(userDocRef, { currentBalance: newBalance }, { merge: true });
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get all transactions for the current user
export const fetchUserTransactions = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'User not authenticated' };

    const transactionsRef = collection(doc(paymentsCollection, currentUser.uid), 'transactions');
    const q = query(transactionsRef, orderBy('createdAt', 'desc'));
    const transactionsSnapshot = await getDocs(q);

    const transactions = transactionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    return { success: true, transactions };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Get current balance for the current user
export const fetchCurrentUserBalance = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'User not authenticated' };

    const userDocRef = doc(paymentsCollection, currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    const currentBalance = userDoc.exists() ? userDoc.data().currentBalance || 0 : 0;

    return { success: true, currentBalance };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Admin: Fetch all users' transactions
export const fetchAllUserTransactions = async () => {
  try {
    const usersSnapshot = await getDocs(paymentsCollection);
    const allTransactions = [];

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const transactionsRef = collection(doc(paymentsCollection, userId), 'transactions');
      const q = query(transactionsRef, orderBy('createdAt', 'desc'));
      const transactionsSnapshot = await getDocs(q);

      const userTransactions = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        userId,
        ...doc.data(),
      }));

      allTransactions.push(...userTransactions);
    }

    // Sort all transactions by creation date
    allTransactions.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return b.createdAt.toMillis() - a.createdAt.toMillis();
      }
      return 0;
    });

    return { success: true, transactions: allTransactions };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Admin: Fetch all users' balances
export const fetchAllUserBalances = async () => {
  try {
    const snapshot = await getDocs(paymentsCollection);

    const balances = snapshot.docs.map(doc => ({
      userId: doc.id,
      currentBalance: doc.data().currentBalance || 0,
    }));

    return { success: true, balances };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Admin: Get transaction statistics
export const getTransactionStats = async () => {
  try {
    const result = await fetchAllUserTransactions();
    if (!result.success) {
      return { success: false, error: result.error };
    }

    const transactions = result.transactions;
    const stats = {
      total: transactions.length,
      successful: transactions.filter(t => t.type === 'credit').length,
      pending: 0, // You can add pending status logic
      failed: transactions.filter(t => t.type === 'debit' && t.description?.includes('failed')).length,
      refunded: transactions.filter(t => t.description?.includes('refund')).length,
    };

    return { success: true, stats };
  } catch (error) {
    return { success: false, error: error.message || error };
  }
};

// Helper function to format transaction data for display
export const formatTransactionForDisplay = (transaction) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB');
  };

  const getStatus = (transaction) => {
    if (transaction.type === 'credit') return 'Successful';
    if (transaction.description?.includes('refund')) return 'Refunded';
    if (transaction.description?.includes('failed')) return 'Failed';
    return 'Successful';
  };

  const getMethod = (paymentdata) => {
    if (!paymentdata) return 'N/A';
    return paymentdata.method || 'Unknown';
  };

  return {
    id: `#TXN${transaction.id.slice(-6).toUpperCase()}`,
    user: transaction.userEmail || 'Unknown User',
    amount: `₨ ${transaction.amount.toLocaleString()}`,
    status: getStatus(transaction),
    method: getMethod(transaction.paymentdata),
    date: formatDate(transaction.createdAt),
    originalData: transaction,
  };
};